import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

// Track in-progress namespace loads to prevent duplicate API calls
const pendingLoads = new Map();

/**
 * Hook to lazy-load translation namespaces on component mount
 * This prevents loading all namespaces globally and instead loads them as needed
 * Includes request deduplication to prevent parallel duplicate requests
 *
 * @param {string|string[]} namespaces - Namespace name(s) to load
 * @returns {object} Translation object with t function
 *
 * @example
 * const { t } = useTranslationNamespace('earning-rules');
 * const { t, i18n } = useTranslationNamespace(['products', 'campaigns']);
 */
export const useTranslationNamespace = (namespaces) => {
  const namespacesArray = Array.isArray(namespaces) ? namespaces : [namespaces];
  const translation = useTranslation(namespacesArray);
  const { i18n } = translation;
  const isLoadingRef = useRef(false);

  // Load namespaces on mount and when language changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Wait for i18n to be initialized with a language
    if (!i18n.language) return;

    namespacesArray.forEach(ns => {
      if (!i18n.hasResourceBundle(i18n.language, ns)) {
        // Check if this namespace is already being loaded (request deduplication)
        const loadKey = `${i18n.language}:${ns}`;
        if (pendingLoads.has(loadKey)) {
          return; // Skip, already loading
        }

        // Mark as loading
        pendingLoads.set(loadKey, true);
        isLoadingRef.current = true;

        i18n.loadNamespaces(ns)
          .then(() => {
            pendingLoads.delete(loadKey);
            isLoadingRef.current = false;
          })
          .catch(err => {
            console.warn(`Failed to load namespace '${ns}':`, err);
            pendingLoads.delete(loadKey);
            isLoadingRef.current = false;
          });
      }
    });

    // Cleanup on unmount
    return () => {
      isLoadingRef.current = false;
    };
  }, [namespacesArray, i18n.language]);

  return translation;
};

export default useTranslationNamespace;
